import React, { useState, useRef, useEffect, useCallback } from 'react'; // Добавлен useCallback
import { Send } from 'lucide-react';
import Button from '../ui/Button';
import SectionTitle from '../ui/SectionTitle';
import { DATA } from '../../data';
import { getTgLink } from '../../utils/telegram';
import { Program } from '../../types';

// Расширяем интерфейс Window, чтобы добавить нашу функцию (для TS)
declare global {
  interface Window {
    scrollToProgramByTitle: (title: string) => void;
  }
}

const Programs: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Обобщенная функция для прокрутки к индексу
  const scrollToIndex = useCallback((index: number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardContainer = container.children[index] as HTMLElement;
      
      if (cardContainer) {
        // Мы работаем с мобильной каруселью (md:hidden)
        const card = cardContainer.children[0] as HTMLElement; // Вложенный div с карточкой
        const cardWidth = card.clientWidth;
        const containerWidth = container.offsetWidth;
        
        // Смещение cardContainer относительно начала scrollContainer
        const offsetLeft = cardContainer.offsetLeft;
        
        // Вычисляем scrollLeft для центрирования карточки
        const scrollLeft = offsetLeft - (containerWidth - cardWidth) / 2;
        
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        setCurrentIndex(index);
      }
    }
  }, []); // Пустой массив зависимостей, так как не зависит от стейта/пропсов

  // Функция для определения текущего индекса при скролле (debounce/throttle не используется для простоты)
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollLeft = container.scrollLeft;
      const cardContainer = container.children[0] as HTMLElement;
      const cardWidth = cardContainer?.offsetWidth || 0; 
      
      if (cardWidth > 0) {
        const index = Math.round(scrollLeft / cardWidth);
        const clampedIndex = Math.max(0, Math.min(index, DATA.programs.length - 1));
        setCurrentIndex(clampedIndex);
      }
    }
  };

  // Функция, которую мы сделаем глобальной
  const scrollToProgramByTitle = useCallback((title: string) => {
    // Находим индекс программы по названию
    const index = DATA.programs.findIndex((prog: Program) => prog.title === title);
    if (index !== -1) {
      scrollToIndex(index);
    } else {
      console.warn(`Program with title "${title}" not found in DATA.programs.`);
    }
  }, [scrollToIndex]);

  // Эффект для установки глобальной функции и очистки
  useEffect(() => {
    // Устанавливаем глобальную функцию, которая может быть вызвана из Footer
    window.scrollToProgramByTitle = scrollToProgramByTitle;

    // Очистка при размонтировании
    return () => {
      // @ts-ignore
      delete window.scrollToProgramByTitle;
    };
  }, [scrollToProgramByTitle]); // Зависимость от scrollToProgramByTitle

  // Эффект для обработчика скролла
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Эффект для начальной прокрутки
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToIndex(0);
    }, 100);
    return () => clearTimeout(timer);
  }, [scrollToIndex]);

  return (
    <section id="programs" className="py-24 lg:py-36 2xl:py-48 bg-brand-white sticky top">
      <div className="grid-system !gap-0">
        <div className="grid-text mb-16">
          <SectionTitle 
            title="Направления" 
            subtitle="Открой свой стиль в движении. Каждое направление — это отдельная история, которую ты сможешь рассказать через танец" 
          />
        </div>
        
        {/* ... (Остальной код Programs.tsx остается без изменений) ... */}
        
        <div className="grid-content">
          {/* Desktop/Tablet (>= 768px, но стили скрывают его на md, который вы хотели изменить) */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-8">
            {DATA.programs.map((prog: Program) => (
              <div 
                key={prog.id} 
                className="group relative rounded-4xl overflow-hidden aspect-square duration-300"
              >
                <img 
                  src={prog.img} 
                  alt={prog.title} 
                  className="absolute inset-0 w-full h-full object-cover" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/100 via-brand-dark/50 to-transparent opacity-100" />
                
                <div className="absolute inset-0 p-8 flex flex-col justify-end text-brand-white">
                  {prog.badge && (
                    <span className="absolute top-6 right-6 bg-brand-secondary text-brand-white text-sm font-bold px-3 py-1.5 rounded-full tracking-wider">
                      {prog.badge}
                    </span>
                  )}
                  <h3 className="h3 font-bold mb-2">
                    {prog.title}
                  </h3>
                  <p className="text-brand-white/50 body-sm mb-6 leading-relaxed">
                    {prog.desc}
                  </p>
                  <Button 
                    variant="telegram"
                    className="!py-2 !px-4 !text-xs !rounded-xl w-fit"
                    href={getTgLink(`Привет! Хочу на направление ${prog.title}`)}
                  >
                    Записаться через Telegram
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Карусель (md:hidden) - это то, что отображается на 360px и теперь на 768px */}
      <div className="md:hidden w-full">
        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto pb-8 snap-x no-scrollbar scroll-smooth w-full px-4"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none'
          }}
        >
          {DATA.programs.map((prog: Program, index) => (
            <div 
              key={prog.id} 
              className="flex-none w-[calc(100vw-32px)] snap-center mx-2"
            >
              <div className="relative rounded-4xl overflow-hidden aspect-square shadow-soft w-full">
                <img 
                  src={prog.img} 
                  alt={prog.title} 
                  className="absolute inset-0 w-full h-full object-cover" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/100 via-brand-dark/50 to-transparent opacity-100" />
                
                <div className="absolute inset-0 p-6 flex flex-col justify-end text-brand-white">
                  {prog.badge && (
                    <span className="absolute top-4 right-4 bg-brand-secondary text-brand-white text-sm font-bold px-3 py-1.5 rounded-full tracking-wider">
                      {prog.badge}
                    </span>
                  )}
                  <h3 className="h3 font-bold mb-2">
                    {prog.title}
                  </h3>
                  <p className="text-brand-white/50 body-sm mb-4 leading-relaxed">
                    {prog.desc}
                  </p>
                  <Button 
                    variant="telegram"
                    className="!py-2 !px-4 !text-xs !rounded-xl w-fit"
                    href={getTgLink(`Привет! Хочу на направление ${prog.title}`)}
                  >
                    Записаться через Telegram
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center gap-2 ">
          {DATA.programs.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentIndex ? 'bg-brand-primary' : 'bg-brand-dark/20'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Кнопка "Узнать расписание" тоже ВНЕ сетки */}
      <div className="grid-system">
        <div className="grid-text text-center mt-8 md:mt-12">
          <Button 
            variant="secondary" 
            className="w-full md:w-auto" 
            href={getTgLink("Привет! Хочу узнать расписание тренировок")}
          >
            Узнать расписание
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Programs;